// src/components/Tenants/Tenants.tsx
import React, { useState, useEffect } from 'react';
import { tenantService } from '../../services/tenantService';
import { Tenant, CreateTenantRequest, UpdateTenantRequest } from '../../types';
import { Plus, Edit, Building2, Users, Settings, Key } from 'lucide-react';
import './Tenants.css';

const Tenants: React.FC = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal states
  const [showNewTenantModal, setShowNewTenantModal] = useState(false);
  const [showEditTenantModal, setShowEditTenantModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);

  // Form states
  const [newTenantForm, setNewTenantForm] = useState<CreateTenantRequest>({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    adminUsername: '',
    adminPassword: '',
    adminEmail: ''
  });

  const [editTenantForm, setEditTenantForm] = useState<UpdateTenantRequest>({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    isActive: true
  });

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await tenantService.getAll();
      setTenants(response.data);
    } catch (error: any) {
      setError('Failed to load tenants');
      console.error('Error fetching tenants:', error);
    } finally {
      setLoading(false);
    }
  };

  const createTenant = async () => {
    if (!newTenantForm.companyName || !newTenantForm.adminUsername || !newTenantForm.adminPassword || !newTenantForm.adminEmail) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      await tenantService.create(newTenantForm);
      setShowNewTenantModal(false);
      setNewTenantForm({
        companyName: '',
        contactName: '',
        email: '',
        phone: '',
        adminUsername: '',
        adminPassword: '',
        adminEmail: ''
      });
      fetchTenants();
    } catch (error: any) {
      alert('Failed to create tenant: ' + (error.response?.data?.message || error.message));
    }
  };

  const openEditModal = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setEditTenantForm({
      companyName: tenant.companyName,
      contactName: tenant.contactName || '',
      email: tenant.email || '',
      phone: tenant.phone || '',
      isActive: tenant.isActive
    });
    setShowEditTenantModal(true);
  };

  const updateTenant = async () => {
    if (!selectedTenant) return;

    try {
      await tenantService.update(selectedTenant.id, editTenantForm);
      setShowEditTenantModal(false);
      setSelectedTenant(null);
      fetchTenants();
    } catch (error: any) {
      alert('Failed to update tenant: ' + (error.response?.data?.message || error.message));
    }
  };

  if (loading) return <div className="loading">Loading tenants...</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="tenants">
      <div className="page-header">
        <h2 className="page-title">Tenant Management</h2>
        <button className="btn btn-primary" onClick={() => setShowNewTenantModal(true)}>
          <Plus size={16} />
          Add Tenant
        </button>
      </div>

      <div className="tenants-grid">
        {tenants.map(tenant => (
          <div key={tenant.id} className={`tenant-card ${!tenant.isActive ? 'tenant-inactive' : ''}`}>
            <div className="tenant-card-header">
              <div className="tenant-name-row">
                <Building2 size={20} />
                <h3>{tenant.companyName}</h3>
              </div>
              <span className={`status-badge ${tenant.isActive ? 'status-active' : 'status-inactive'}`}>
                {tenant.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>

            {tenant.contactName && (
              <p className="tenant-contact">{tenant.contactName}</p>
            )}
            {tenant.email && (
              <p className="tenant-email">{tenant.email}</p>
            )}

            <p className="tenant-id" style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#9ca3af', marginBottom: '0.75rem' }}>
              ID: {tenant.id}
            </p>

            <div className="tenant-stats">
              <div className="tenant-stat">
                <Users size={16} />
                <span>{tenant.userCount} users</span>
              </div>
              <div className="tenant-stat">
                <Settings size={16} />
                <span>{tenant.serviceCount} services</span>
              </div>
              <div className="tenant-stat">
                <Key size={16} />
                <span>{tenant.consumerCount} consumers</span>
              </div>
            </div>

            <div className="tenant-meta">
              Created: {new Date(tenant.createdDate).toLocaleDateString()}
            </div>

            <div className="tenant-actions">
              <button className="btn btn-secondary btn-sm" onClick={() => openEditModal(tenant)}>
                <Edit size={16} />
                Edit
              </button>
            </div>
          </div>
        ))}
      </div>

      {tenants.length === 0 && (
        <div className="empty-state">
          <Building2 className="empty-state-icon" size={48} />
          <p className="empty-state-title">No Tenants</p>
          <p className="empty-state-description">Create your first tenant to get started.</p>
        </div>
      )}

      {/* New Tenant Modal */}
      {showNewTenantModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3 className="modal-title">Add New Tenant</h3>
            </div>
            <div className="modal-body">
              <h4 className="form-section-title">Organisation Details</h4>
              <div className="form-group">
                <label className="form-label">Company Name *</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g., Pinage Holdings"
                  value={newTenantForm.companyName}
                  onChange={(e) => setNewTenantForm({...newTenantForm, companyName: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Contact Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={newTenantForm.contactName}
                  onChange={(e) => setNewTenantForm({...newTenantForm, contactName: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={newTenantForm.email}
                  onChange={(e) => setNewTenantForm({...newTenantForm, email: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input
                  type="tel"
                  className="form-input"
                  value={newTenantForm.phone}
                  onChange={(e) => setNewTenantForm({...newTenantForm, phone: e.target.value})}
                />
              </div>

              <h4 className="form-section-title">Tenant Admin Account</h4>
              <p className="form-hint">This user will manage the tenant's consumers, API keys and services.</p>
              <div className="form-group">
                <label className="form-label">Admin Username *</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g., pinage.admin"
                  value={newTenantForm.adminUsername}
                  onChange={(e) => setNewTenantForm({...newTenantForm, adminUsername: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Admin Password *</label>
                <input
                  type="password"
                  className="form-input"
                  value={newTenantForm.adminPassword}
                  onChange={(e) => setNewTenantForm({...newTenantForm, adminPassword: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Admin Email *</label>
                <input
                  type="email"
                  className="form-input"
                  value={newTenantForm.adminEmail}
                  onChange={(e) => setNewTenantForm({...newTenantForm, adminEmail: e.target.value})}
                  required
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowNewTenantModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={createTenant}>
                Create Tenant
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Tenant Modal */}
      {showEditTenantModal && selectedTenant && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3 className="modal-title">Edit Tenant</h3>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Company Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={editTenantForm.companyName}
                  onChange={(e) => setEditTenantForm({...editTenantForm, companyName: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Contact Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={editTenantForm.contactName}
                  onChange={(e) => setEditTenantForm({...editTenantForm, contactName: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={editTenantForm.email}
                  onChange={(e) => setEditTenantForm({...editTenantForm, email: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input
                  type="tel"
                  className="form-input"
                  value={editTenantForm.phone}
                  onChange={(e) => setEditTenantForm({...editTenantForm, phone: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={editTenantForm.isActive}
                    onChange={(e) => setEditTenantForm({...editTenantForm, isActive: e.target.checked})}
                    className="form-checkbox"
                  />
                  Active
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowEditTenantModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={updateTenant}>
                Update Tenant
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tenants;
